import { useEffect, useRef, useState } from "react";
import { CalendarClock, X } from "lucide-react";

import { cn } from "@/lib/cn";

interface InlineDateProps {
  value: string | null;
  ariaLabel: string;
  placeholder?: string;
  onCommit: (next: string | null) => Promise<boolean>;
  saving?: boolean;
  error?: string | null;
}

function toInputValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function fromInputValue(v: string): string | null {
  if (!v) return null;
  const d = new Date(`${v}T23:59:59.000Z`);
  return d.toISOString();
}

function formatChip(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function InlineDate({
  value,
  ariaLabel,
  placeholder = "Указать дату",
  onCommit,
  saving = false,
  error = null,
}: InlineDateProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toInputValue(value));
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(toInputValue(value));
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.showPicker?.();
    }
  }, [editing]);

  const commit = async (next: string) => {
    const iso = fromInputValue(next);
    const ok = await onCommit(iso);
    if (ok) setEditing(false);
  };

  const clear = async () => {
    const ok = await onCommit(null);
    if (ok) setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={ariaLabel}
          className={cn(
            "inline-flex min-h-touch items-center gap-2 rounded-card px-3 py-1",
            "hover:bg-sbg/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            value ? "bg-sbg" : "border border-dashed border-hint/50 text-hint",
          )}
        >
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          <span>{value ? formatChip(value) : placeholder}</span>
        </button>
        {value && (
          <button
            type="button"
            onClick={() => void clear()}
            aria-label="Очистить дату"
            disabled={saving}
            className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-card text-hint hover:bg-sbg/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <input
        ref={inputRef}
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit(draft)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setEditing(false);
            setDraft(toInputValue(value));
          }
        }}
        aria-label={ariaLabel}
        disabled={saving}
        className="block min-h-touch rounded-card border border-hint/30 bg-sbg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
      />
      {error && (
        <span className="text-chip text-dstr" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
